/**
 * admin/src/pages/StudentDetailPage.tsx
 * Chi tiết 1 học sinh: thông tin cơ bản + form sửa + stats + recent attempts
 *
 * Route: /admin/students/:id
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStudent, updateStudent, updateStudentParent } from '../hooks/useStudents';
import {
  TierBadge,
  LoadingSpinner,
  ErrorBanner,
  Badge,
  toast,
  formatDate,
  timeAgo,
} from '../components/ui/index';

// ─── Avatar & Theme config ────────────────────────────────────────────────────

const AVATAR_OPTIONS = [
  { value: 'cat',     label: '🐱 Mèo' },
  { value: 'dog',     label: '🐶 Chó' },
  { value: 'fox',     label: '🦊 Cáo' },
  { value: 'rabbit',  label: '🐰 Thỏ' },
  { value: 'bear',    label: '🐻 Gấu' },
  { value: 'panda',   label: '🐼 Gấu trúc' },
  { value: 'owl',     label: '🦉 Cú' },
  { value: 'penguin', label: '🐧 Chim cánh cụt' },
  { value: 'lion',    label: '🦁 Sư tử' },
  { value: 'tiger',   label: '🐯 Hổ' },
  { value: 'dragon',  label: '🐲 Rồng' },
  { value: 'unicorn', label: '🦄 Kỳ lân' },
];

const THEME_OPTIONS = [
  { value: 'purple', label: '💜 Purple' },
  { value: 'ocean',  label: '🌊 Ocean' },
  { value: 'sakura', label: '🌸 Sakura' },
  { value: 'forest', label: '🌿 Forest' },
  { value: 'night',  label: '🌙 Night' },
  { value: 'sunset', label: '🌅 Sunset' },
];

const AVATAR_EMOJIS: Record<string, string> = {
  cat: '🐱', dog: '🐶', fox: '🦊', rabbit: '🐰',
  bear: '🐻', panda: '🐼', owl: '🦉', penguin: '🐧',
  lion: '🦁', tiger: '🐯', dragon: '🐲', unicorn: '🦄',
};

// ─── Skill level helpers ──────────────────────────────────────────────────────

function SkillBar({ label, value }: { label: string; value: number }) {
  const pct = Math.min(100, (value / 5) * 100);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-2)' }}>{label}</span>
        <span style={{ color: 'var(--text-3)' }}>{value}/5</span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: pct >= 80 ? 'var(--success)' : pct >= 40 ? 'var(--accent)' : 'var(--text-3)',
          borderRadius: 4,
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function StudentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { student, isLoading, error, refetch } = useStudent(id);

  // ── Form state: Student info ───────────────────────────────────────────────
  const [studentForm, setStudentForm] = useState<{
    display_name: string;
    avatar_id: string;
    theme: string;
  } | null>(null);
  const [savingStudent, setSavingStudent] = useState(false);

  // ── Form state: Parent info ────────────────────────────────────────────────
  const [parentForm, setParentForm] = useState<{
    name: string;
    notification_email: string;
  } | null>(null);
  const [savingParent, setSavingParent] = useState(false);

  // Khởi tạo form khi data load xong (chỉ lần đầu)
  if (student && !studentForm) {
    setStudentForm({
      display_name: student.display_name,
      avatar_id: student.avatar_id,
      theme: student.theme,
    });
  }
  if (student && !parentForm) {
    setParentForm({
      name: student.parent_name ?? '',
      notification_email: '', // Không hiện email thật — bảo mật
    });
  }

  // ── Handlers ───────────────────────────────────────────────────────────────

  async function handleSaveStudent() {
    if (!id || !studentForm) return;
    if (!studentForm.display_name.trim()) {
      toast('Tên học sinh không được để trống', 'error');
      return;
    }
    setSavingStudent(true);
    try {
      await updateStudent(id, {
        display_name: studentForm.display_name.trim(),
        avatar_id: studentForm.avatar_id,
        theme: studentForm.theme,
      });
      toast('✅ Đã cập nhật thông tin học sinh', 'success');
      refetch();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi cập nhật', 'error');
    } finally {
      setSavingStudent(false);
    }
  }

  async function handleSaveParent() {
    if (!id || !parentForm) return;
    setSavingParent(true);
    try {
      await updateStudentParent(id, {
        name: parentForm.name || undefined,
        notification_email: parentForm.notification_email || null,
      });
      toast('✅ Đã cập nhật thông tin phụ huynh', 'success');
      refetch();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi cập nhật', 'error');
    } finally {
      setSavingParent(false);
    }
  }

  // ── Render states ──────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex-center" style={{ height: 300 }}>
        <LoadingSpinner size={36} />
      </div>
    );
  }

  if (error || !student) {
    return (
      <div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
          ← Quay lại
        </button>
        <ErrorBanner message={error ?? 'Không tìm thấy học sinh'} onRetry={refetch} />
      </div>
    );
  }

  const avatarEmoji = AVATAR_EMOJIS[student.avatar_id] ?? '🧒';

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header__left">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 8 }}>
            ← Quay lại danh sách
          </button>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 36 }}>{avatarEmoji}</span>
            {student.display_name}
            <TierBadge tier={student.parent_tier} />
          </h1>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
            Phụ huynh: <strong>{student.parent_name || '(chưa đặt tên)'}</strong>
            {' · '}{student.parent_email}
            {' · '}Tạo {formatDate(student.created_at)}
          </p>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 24,
      }}>
        {[
          { label: '⭐ Sao', value: student.total_stars.toLocaleString('vi-VN') },
          { label: '🔥 Streak', value: `${student.current_streak} ngày` },
          { label: '📝 Sessions', value: student.total_sessions.toLocaleString('vi-VN') },
          { label: '🕐 Hoạt động', value: student.last_active ? timeAgo(student.last_active) : 'Chưa có' },
        ].map(({ label, value }) => (
          <div key={label} className="card" style={{ textAlign: 'center', padding: '16px 12px' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)' }}>{value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Edit forms: 2-column grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>

        {/* Form: Thông tin học sinh */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>✏️ Thông tin học sinh</h3>
          {studentForm && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="form-label">Tên hiển thị</label>
                <input
                  className="input"
                  value={studentForm.display_name}
                  onChange={(e) => setStudentForm(f => f ? { ...f, display_name: e.target.value } : f)}
                  placeholder="Nhập tên học sinh..."
                  maxLength={50}
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                  {studentForm.display_name.length}/50 ký tự
                </div>
              </div>

              <div>
                <label className="form-label">Avatar</label>
                <select
                  className="input"
                  value={studentForm.avatar_id}
                  onChange={(e) => setStudentForm(f => f ? { ...f, avatar_id: e.target.value } : f)}
                  style={{ width: '100%' }}
                >
                  {AVATAR_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Theme màu</label>
                <select
                  className="input"
                  value={studentForm.theme}
                  onChange={(e) => setStudentForm(f => f ? { ...f, theme: e.target.value } : f)}
                  style={{ width: '100%' }}
                >
                  {THEME_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleSaveStudent}
                disabled={savingStudent}
              >
                {savingStudent ? '⏳ Đang lưu...' : '💾 Lưu thông tin học sinh'}
              </button>
            </div>
          )}
        </div>

        {/* Form: Thông tin phụ huynh */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>👤 Thông tin phụ huynh</h3>
          <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--text-3)' }}>Email đăng ký (không thể sửa): </span>
            <strong>{student.parent_email}</strong>
          </div>
          {parentForm && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="form-label">Tên phụ huynh</label>
                <input
                  className="input"
                  value={parentForm.name}
                  onChange={(e) => setParentForm(f => f ? { ...f, name: e.target.value } : f)}
                  placeholder="Nhập tên phụ huynh..."
                  maxLength={100}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label className="form-label">Email nhận báo cáo tuần</label>
                <input
                  className="input"
                  type="email"
                  value={parentForm.notification_email}
                  onChange={(e) => setParentForm(f => f ? { ...f, notification_email: e.target.value } : f)}
                  placeholder="Để trống = tắt báo cáo email"
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                  Để trống để tắt nhận báo cáo học tập hàng tuần
                </div>
              </div>

              <button
                className="btn btn-secondary"
                onClick={handleSaveParent}
                disabled={savingParent}
              >
                {savingParent ? '⏳ Đang lưu...' : '💾 Lưu thông tin phụ huynh'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Skill levels ── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 16 }}>📊 Trình độ kỹ năng</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 32px' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8 }}>🎧 Listening</div>
            <SkillBar label="Part 1" value={student.lvl_listening_p1} />
            <SkillBar label="Part 2" value={student.lvl_listening_p2} />
            <SkillBar label="Part 3" value={student.lvl_listening_p3} />
            <SkillBar label="Part 4" value={student.lvl_listening_p4} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8 }}>📖 Reading</div>
            <SkillBar label="Part 1" value={student.lvl_reading_p1} />
            <SkillBar label="Part 2" value={student.lvl_reading_p2} />
            <SkillBar label="Part 3" value={student.lvl_reading_p3} />
            <SkillBar label="Part 4" value={student.lvl_reading_p4} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8 }}>✏️ Writing</div>
            <SkillBar label="Part 1" value={student.lvl_writing_p1} />
          </div>
        </div>
      </div>

      {/* ── Dream Goal ── */}
      {student.dream_goal && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12 }}>🎁 Hộp Quà Ước Mơ</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{student.dream_goal.reward_label}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                {student.dream_goal.exam_id} · {student.dream_goal.goal_type} · Mục tiêu: {student.dream_goal.goal_target}
              </div>
            </div>
            <Badge variant={
              student.dream_goal.status === 'approved' ? 'success' :
              student.dream_goal.status === 'achieved' ? 'success' : 'warning'
            }>
              {student.dream_goal.status === 'pending' ? '⏳ Chờ duyệt' :
               student.dream_goal.status === 'approved' ? '✅ Đã duyệt' :
               student.dream_goal.status === 'achieved' ? '🎉 Đạt được' : student.dream_goal.status}
            </Badge>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{student.dream_goal.current_progress}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>/ {student.dream_goal.goal_target}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Recent Attempts ── */}
      <div className="card">
        <h3 style={{ marginBottom: 16 }}>
          📝 20 bài làm gần nhất
          <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 8 }}>
            ({student.recent_attempts.length} bài)
          </span>
        </h3>
        {student.recent_attempts.length === 0 ? (
          <div className="empty-state" style={{ padding: 32 }}>
            <div className="empty-state__icon">📭</div>
            <div className="empty-state__text">Chưa làm bài nào</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {student.recent_attempts.map(attempt => {
              const pct = attempt.max_score ? Math.round((attempt.score ?? 0) / attempt.max_score * 100) : 0;
              return (
                <div key={attempt.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  background: 'var(--bg-elevated)',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{attempt.quiz_id}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                      {attempt.skill} · Part {attempt.part_number ?? '?'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 60 }}>
                    <div style={{ fontWeight: 600, color: pct >= 80 ? 'var(--success)' : pct >= 50 ? 'var(--accent)' : 'var(--danger)' }}>
                      {attempt.score ?? 0}/{attempt.max_score ?? '?'}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{pct}%</div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 50 }}>
                    <div style={{ fontWeight: 600, color: 'var(--warning)' }}>⭐ {attempt.stars_earned}</div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', minWidth: 100, textAlign: 'right' }}>
                    {timeAgo(attempt.completed_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
